import { useEffect, useState } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { ErrorState, Skeleton } from '@/components/States'

// A21.19 — Musiqa kutubxonasi (moderatsiya). Real CRUD backend ustida:
//   GET    /admin/music/songs?status=       → {songs}
//   POST   /admin/music/songs/:id/moderate  {decision: approve|reject}
//   POST   /admin/music/songs/:id/premium   {premium: bool}
//   DELETE /admin/music/songs/:id
interface Song {
  id: number
  title: string
  artist: string
  genre?: string
  duration_sec: number
  is_premium: boolean
  status: string
  plays: number
}

const STATUSES = ['', 'pending', 'approved', 'rejected'] as const

function mmss(s: number): string {
  if (!s || s < 0) return '—'
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${r < 10 ? '0' : ''}${r}`
}

export function MusicLibrary() {
  const t = useT()
  const [songs, setSongs] = useState<Song[]>([])
  const [status, setStatus] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [busy, setBusy] = useState<number | null>(null)
  const [confirmDel, setConfirmDel] = useState<number | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    api
      .get<{ songs: Song[] }>(`/admin/music/songs?status=${status}`, { signal: ac.signal })
      .then((d) => {
        if (alive) setSongs(d.songs ?? [])
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [status, reload])

  async function act(id: number, fn: () => Promise<unknown>) {
    setBusy(id)
    try {
      await fn()
      setReload((n) => n + 1)
    } catch {
      /* toast qatlami umumiy; bu yerда jim qayta-yuklaymiz */
      setReload((n) => n + 1)
    } finally {
      setBusy(null)
      setConfirmDel(null)
    }
  }
  const moderate = (id: number, decision: 'approve' | 'reject') =>
    act(id, () => api.post(`/admin/music/songs/${id}/moderate`, { decision }))
  const togglePremium = (s: Song) =>
    act(s.id, () => api.post(`/admin/music/songs/${s.id}/premium`, { premium: !s.is_premium }))
  const del = (id: number) => act(id, () => api.del(`/admin/music/songs/${id}`))

  const statusPill = (st: string) => {
    const cls =
      st === 'approved' ? 'text-success' : st === 'rejected' ? 'text-danger' : 'text-accent'
    const label = st === 'approved' ? t.music.stApproved : st === 'rejected' ? t.music.stRejected : t.music.stPending
    return <span className={`text-xs font-medium ${cls}`}>● {label}</span>
  }

  return (
    <>
      <PageHeader
        title={t.music.title}
        screenId="A21.19"
        description={t.music.note}
        actions={
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-secondary"
          >
            {STATUSES.map((s) => (
              <option key={s || 'all'} value={s}>
                {s === '' ? t.music.stAll : s === 'pending' ? t.music.stPending : s === 'approved' ? t.music.stApproved : t.music.stRejected}
              </option>
            ))}
          </select>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : loading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !songs.length ? (
          <div className="py-10 text-center text-sm text-tertiary">{t.dash.noData}</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border-default">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border-default bg-surface-2 text-left text-xs text-tertiary">
                  <th className="px-3 py-2 font-medium">{t.music.colTitle}</th>
                  <th className="px-3 py-2 font-medium">{t.music.colArtist}</th>
                  <th className="px-3 py-2 font-medium">{t.music.colGenre}</th>
                  <th className="px-3 py-2 font-medium">{t.music.colDuration}</th>
                  <th className="px-3 py-2 font-medium">{t.music.colPlays}</th>
                  <th className="px-3 py-2 font-medium">{t.music.colStatus}</th>
                  <th className="px-3 py-2 text-right font-medium">{t.common.actions}</th>
                </tr>
              </thead>
              <tbody>
                {songs.map((s) => (
                  <tr key={s.id} className="border-b border-border-subtle hover:bg-surface-2/50">
                    <td className="px-3 py-2">
                      <span className="text-primary">{s.title}</span>
                      {s.is_premium && (
                        <span className="ml-1.5 rounded-full border border-accent/40 px-1.5 py-0.5 text-[10px] text-accent">
                          {t.music.premium}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-secondary">{s.artist || '—'}</td>
                    <td className="px-3 py-2 text-tertiary">{s.genre || '—'}</td>
                    <td className="px-3 py-2 font-mono tabular text-secondary">{mmss(s.duration_sec)}</td>
                    <td className="px-3 py-2 font-mono tabular text-secondary">{s.plays.toLocaleString()}</td>
                    <td className="px-3 py-2">{statusPill(s.status)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        {s.status !== 'approved' && (
                          <button
                            type="button"
                            disabled={busy === s.id}
                            onClick={() => moderate(s.id, 'approve')}
                            className="rounded-md border border-border-default px-2 py-0.5 text-xs text-success hover:bg-surface-3 disabled:opacity-50"
                          >
                            {t.music.approve}
                          </button>
                        )}
                        {s.status !== 'rejected' && (
                          <button
                            type="button"
                            disabled={busy === s.id}
                            onClick={() => moderate(s.id, 'reject')}
                            className="rounded-md border border-border-default px-2 py-0.5 text-xs text-danger hover:bg-surface-3 disabled:opacity-50"
                          >
                            {t.music.reject}
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={busy === s.id}
                          onClick={() => togglePremium(s)}
                          className="rounded-md border border-border-default px-2 py-0.5 text-xs text-secondary hover:bg-surface-3 disabled:opacity-50"
                        >
                          {s.is_premium ? t.music.unpremium : t.music.makePremium}
                        </button>
                        <button
                          type="button"
                          disabled={busy === s.id}
                          onClick={() => (confirmDel === s.id ? del(s.id) : setConfirmDel(s.id))}
                          className={`rounded-md border px-2 py-0.5 text-xs disabled:opacity-50 ${confirmDel === s.id ? 'border-danger bg-danger/10 text-danger' : 'border-border-default text-danger hover:bg-surface-3'}`}
                        >
                          {confirmDel === s.id ? t.music.confirmDelete : t.common.delete}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
